/**
 * HQ 라우트.
 *
 * 도메인별 마운트 위치:
 *   - hq.newattend.com           → /*    (HQ_BASE = '')
 *   - newattend.com  (레거시)    → /hq/* (HQ_BASE = '/hq')
 *
 * 내부 절대 리다이렉트는 HQ_BASE 변수로 자동 분기.
 */
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { hqAuthService } from '../services/hqAuthService';
import HqLayout from '../components/HqLayout';
import HqLoginPage from '../pages/HqLoginPage';
import HqDashboardPage from '../pages/HqDashboardPage';
import HqStaffPage from '../pages/HqStaffPage';
import HqBrandPage from '../pages/HqBrandPage';
import HqStoresPage from '../pages/HqStoresPage';
import HqStoreCreatePage from '../pages/HqStoreCreatePage';
import HqStoreDetailPage from '../pages/HqStoreDetailPage';
import HqAdmissionMatchPage from '../pages/HqAdmissionMatchPage';
import HqAdmissionInsightsPage from '../pages/HqAdmissionInsightsPage';
import HqPlansPage from '../pages/HqPlansPage';
import HqGatesPage from '../pages/HqGatesPage';
import HqTabletGatesPage from '../pages/HqTabletGatesPage';
import HqNoticesPage from '../pages/HqNoticesPage';
import HqErrorReportsPage from '../pages/HqErrorReportsPage';
import HqSupportPage from '../pages/HqSupportPage';
import HqRevenuePage from '../pages/HqRevenuePage';
import HqExpiryPage from '../pages/HqExpiryPage';
import HqAlimtalkBillsPage from '../pages/HqAlimtalkBillsPage';
import HqSignupsPage from '../pages/HqSignupsPage';
import HqSubscriptionConsentsPage from '../pages/HqSubscriptionConsentsPage';
import HqConsultationsPage from '../pages/HqConsultationsPage';

// hq 서브도메인이면 베이스 경로 없음, 그 외(레거시)는 /hq 접두 유지
const HQ_BASE =
  typeof window !== 'undefined' && window.location.hostname === 'hq.newattend.com'
    ? ''
    : '/hq';

/**
 * HQ 보호 라우트.
 *
 * 보안 강화(2026-07-04): 토큰 "존재"만 보던 옛 방식은 브라우저에 남은 옛 HQ 토큰으로
 * 재로그인 없이 진입되는 세션 위생 문제가 있었다. 이제 진입 시 서버에
 * `/api/v1/hq/auth/me` 를 호출해 토큰의 유효성·만료·본부직원 역할을 실제로 검증한다.
 *   - 검증 성공(hq_admin/hq_staff) → 렌더
 *   - 만료(401)·비-HQ(403)·네트워크 실패 → 토큰 정리 후 로그인으로
 */
const HqProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<'checking' | 'ok' | 'fail'>('checking');

  useEffect(() => {
    let alive = true;

    if (!hqAuthService.isAuthenticated()) {
      setStatus('fail');
      return;
    }

    hqAuthService
      .me()
      .then(() => {
        if (alive) setStatus('ok');
      })
      .catch(() => {
        // 만료(401)·비-HQ(403)·네트워크 오류 → 세션 무효화
        hqAuthService.logout();
        if (alive) setStatus('fail');
      });

    return () => {
      alive = false;
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
        본부 인증 확인 중…
      </div>
    );
  }

  if (status === 'fail') {
    return <Navigate to={`${HQ_BASE}/login`} replace />;
  }

  return <>{children}</>;
};

const HqRoutes: React.FC = () => (
  <Routes>
    <Route path="login" element={<HqLoginPage />} />

    <Route
      element={
        <HqProtected>
          <HqLayout />
        </HqProtected>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<HqDashboardPage />} />
      <Route path="stores" element={<HqStoresPage />} />
      <Route path="signups" element={<HqSignupsPage />} />
      <Route path="revenue" element={<HqRevenuePage />} />
      <Route path="expiry" element={<HqExpiryPage />} />
      <Route path="alimtalk-bills" element={<HqAlimtalkBillsPage />} />
      <Route path="subscription-consents" element={<HqSubscriptionConsentsPage />} />
      <Route path="consultations" element={<HqConsultationsPage />} />
      <Route path="stores/new" element={<HqStoreCreatePage />} />
      <Route path="stores/:id" element={<HqStoreDetailPage />} />
      <Route path="staff" element={<HqStaffPage />} />
      <Route path="plans" element={<HqPlansPage />} />
      <Route path="gates" element={<HqGatesPage />} />
      <Route path="tablet-gates" element={<HqTabletGatesPage />} />
      <Route path="notices" element={<HqNoticesPage />} />
      <Route path="support" element={<HqSupportPage />} />
      <Route path="error-reports" element={<HqErrorReportsPage />} />
      <Route path="brand" element={<HqBrandPage />} />
      <Route path="admission/match" element={<HqAdmissionMatchPage />} />
      <Route path="admission/insights" element={<HqAdmissionInsightsPage />} />
    </Route>

    <Route path="*" element={<Navigate to={`${HQ_BASE}/dashboard`} replace />} />
  </Routes>
);

export default HqRoutes;
